
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import LuxuryButton from "@/components/ui/luxury-button";
import { Link } from "react-router-dom";

const YouTubeVideoHero = () => {
  const [showContent, setShowContent] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    // Show content after short delay
    const contentTimer = setTimeout(() => setShowContent(true), 800);
    
    // Show fallback animation if video doesn't load within 3 seconds
    const fallbackTimer = setTimeout(() => {
      if (!videoLoaded) {
        setShowFallback(true);
      }
    }, 3000);

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(fallbackTimer);
    };
  }, [videoLoaded]);

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* YouTube Video Background */}
      <div className="absolute inset-0 z-0 w-full h-full overflow-hidden">
        <iframe
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '100vw',
            height: '56.25vw',
            minHeight: '100vh',
            minWidth: '177.78vh',
          }}
          src="https://www.youtube.com/embed/upLcQD0dmCI?autoplay=1&mute=1&loop=1&playlist=upLcQD0dmCI&controls=0&showinfo=0&modestbranding=1&iv_load_policy=3&rel=0&start=0"
          title="JUV Background Video"
          allow="autoplay; encrypted-media"
          allowFullScreen
          onLoad={() => setVideoLoaded(true)}
        />
        
        {/* Cinematic video overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
      </div>

      {/* Fallback Cinematic Animation */}
      {showFallback && !videoLoaded && (
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-juv-cream-white via-juv-soft-blush to-juv-rose-clay">
            {/* Animated particles */}
            <motion.div
              className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
                x: [0, 50, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-juv-terracotta/30 rounded-full blur-2xl"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.4, 0.7, 0.4],
                x: [0, -40, 0],
                y: [0, 40, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
            <motion.div
              className="absolute top-1/2 left-1/2 w-48 h-48 bg-juv-soft-blush/40 rounded-full blur-xl"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.2, 0.5, 0.2],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
      )}
      
      {/* Content positioned bottom-left with Apple-style typography */}
      <div className="apple-container pb-20 relative z-10">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ 
              opacity: showContent ? 1 : 0, 
              y: showContent ? 0 : 30 
            }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="apple-spacing-lg"
          >
            <h1 className="apple-headline text-white font-serif drop-shadow-lg">
              Your Glow,<br />
              <span className="font-light">Reimagined.</span>
            </h1>
            
            <p className="apple-subheadline text-white/95 max-w-2xl drop-shadow-md">
              Experience personalized, luxury aesthetic care tailored just for you.
            </p>
            
            <div className="pt-8">
              <LuxuryButton 
                asChild
                size="lg" 
                variant="primary"
                glowEffect
                className="text-lg shadow-apple-xl hover:shadow-apple-xl transform hover:scale-105 transition-all duration-400"
              >
                <Link to="/quiz" className="flex items-center gap-3">
                  Take the Glow Quiz
                </Link>
              </LuxuryButton>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Apple-style scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 1 : 0 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            className="w-1 h-3 bg-white/70 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default YouTubeVideoHero;
